import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/lib/utils';

import { queryKeys } from '@/lib/constants';
import {
  fetchEvents,
  fetchEvent,
  createEvent,
  updateEvent,
  deleteEvent,
  transformToDisplayEvents,
  fetchUsers,
  fetchEventTypes,
} from '../api';
import type { EventFilters, CreateEventDto, UpdateEventDto } from '../types';

export function useEvents(filters: EventFilters) {
  return useQuery({
    queryKey: queryKeys.calendar.events(filters as unknown as Record<string, unknown>),
    queryFn: () => fetchEvents(filters),
  });
}

export function useDisplayEvents(filters: EventFilters) {
  const query = useEvents(filters);

  return {
    ...query,
    data: query.data ? {
      ...query.data,
      displayEvents: transformToDisplayEvents(query.data.results),
    } : undefined,
  };
}

export function useEvent(id: number | undefined) {
  return useQuery({
    queryKey: queryKeys.calendar.event(id),
    queryFn: () => fetchEvent(id!),
    enabled: !!id,
  });
}

export function useCalendarUsers() {
  return useQuery({
    queryKey: queryKeys.calendar.users(),
    queryFn: () => fetchUsers({ page: 0, limit: 100 }),
  });
}

export function useEventTypes() {
  return useQuery({
    queryKey: queryKeys.calendar.eventTypes(),
    queryFn: () => fetchEventTypes(),
  });
}

export function useEventMutations() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const createMutation = useMutation({
    mutationFn: (data: CreateEventDto) => createEvent(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['getAllEvents'] });
      void queryClient.invalidateQueries({ queryKey: ['calendar'] });
      toast.success(t('eventCreatedSuccess'));
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorCreatingEvent')));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateEventDto }) => updateEvent(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['getAllEvents'] });
      void queryClient.invalidateQueries({ queryKey: ['calendar'] });
      toast.success(t('eventUpdatedSuccess'));
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorUpdatingEvent')));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteEvent(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['getAllEvents'] });
      void queryClient.invalidateQueries({ queryKey: ['calendar'] });
      toast.success(t('eventDeletedSuccess'));
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorDeletingEvent')));
    },
  });

  return {
    createMutation,
    updateMutation,
    deleteMutation,
  };
}
